import { useEffect, useState } from "react";
import { Lightbulb, ChevronRight, Loader2 } from "lucide-react";
import { feedback } from "./lib/api.js";
import { Modal } from "./ui.jsx";
import { C, inputStyle } from "./theme.js";

/* ============================================================================
   הצעה לשיפור — פאנל משותף לבעל הנכס ולדייר, בתוך חלון מודאלי.

   ערוץ נפרד לגמרי מהודעות דייר↔בעל-נכס: כאן שני הצדדים האפשריים הם
   משתמש קצה מול בעל הפלטפורמה, ולכן גם התשובה מגיעה ממסך "הצעות
   לשיפור" הנפרד ב-Admin.jsx ולא מתיבת ההודעות הרגילה.

   role קובע רק איזה API להשתמש בו (owner/tenant) — השרת מזהה את
   המשתמש עצמו מהעוגייה, לא מהנתיב. כל צד רואה רק את האשכולות שהוא
   עצמו פתח.
   ============================================================================ */

export default function FeedbackPanel({ role }) {
  const api = feedback[role];
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // { enabled, threads }
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () => api.threads().then(setState).catch(() => {});
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openThread = (id) => {
    setActiveId(id);
    setMessages(null);
    api.thread(id).then((r) => setMessages(r.messages)).catch(() => setMessages([]));
  };

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      if (activeId) {
        await api.reply(activeId, t);
        const r = await api.thread(activeId);
        setMessages(r.messages);
      } else {
        const r = await api.send(t);
        openThread(r.id);
      }
      setText("");
      reload();
    } catch {
      // שגיאת רשת חולפת — הטקסט נשאר בתיבה, המשתמש יכול לנסות שוב.
    }
    setBusy(false);
  };

  // עוד לא נטען, או שאין למשתמש הזה שום היסטוריה והמתג כבוי — הכפתור לא מוצג כלל.
  if (!state) return null;
  if (!state.enabled && state.threads.length === 0) return null;

  const unread = state.threads.reduce((n, t) => n + t.unread, 0);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="text-xs flex items-center gap-1" style={{ color: "rgba(255,255,255,.72)" }}>
        <Lightbulb size={12} /> הצעות לשיפור
        {unread > 0 && (
          <span className="text-[10px] rounded-full px-1.5 py-0.5" style={{ background: C.teal, color: "#fff" }}>
            {unread}
          </span>
        )}
      </button>

      {open && (
        <Modal title="הצעות לשיפור" onClose={() => { setOpen(false); setActiveId(null); setMessages(null); }}>
          {activeId ? (
            <>
              <button type="button" onClick={() => { setActiveId(null); setMessages(null); }}
                className="text-[12px] flex items-center gap-1 mb-2" style={{ color: C.muted }}>
                <ChevronRight size={13} /> כל ההצעות שלי
              </button>
              <div className="space-y-1.5 mb-3 max-h-72 overflow-y-auto">
                {messages == null ? (
                  <Loader2 size={16} className="animate-spin" style={{ color: C.muted }} />
                ) : messages.map((m) => (
                  <div key={m.id} className="text-sm rounded-lg px-2.5 py-1.5"
                    style={{ background: m.fromAdmin ? "#EAF3EE" : "#F1F4F1" }}>
                    {m.fromAdmin && (
                      <div className="text-[10px] mb-0.5" style={{ color: C.paid }}>בעל הפלטפורמה</div>
                    )}
                    {m.text}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="text-[12px] mb-3" style={{ color: C.muted }}>
                מלל חופשי לבעל הפלטפורמה — מה היה עוזר לכם בשימוש באפליקציה.
              </p>
              {state.threads.length > 0 && (
                <div className="space-y-1 mb-3">
                  {state.threads.map((t) => (
                    <button key={t.id} type="button" onClick={() => openThread(t.id)}
                      className="w-full text-right text-[12px] rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-2"
                      style={{ background: "#F1F4F1" }}>
                      <span className="truncate">{t.lastFromAdmin ? "בעל הפלטפורמה: " : ""}{t.lastText}</span>
                      {t.unread > 0 && (
                        <span className="text-[10px] rounded-full px-1.5 py-0.5 shrink-0" style={{ background: C.teal, color: "#fff" }}>
                          {t.unread}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {state.enabled ? (
            <div className="flex gap-2 items-end">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                placeholder={activeId ? "הוסיפו תגובה…" : "כתבו כאן הצעה לשיפור הפלטפורמה…"}
                className="flex-1 rounded-lg p-2 text-sm" style={inputStyle} />
              <button type="button" onClick={send} disabled={busy || !text.trim()}
                className="rounded-lg px-3 py-2 text-sm shrink-0"
                style={{ background: C.teal, color: "#fff", opacity: busy || !text.trim() ? 0.6 : 1 }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : "שליחה"}
              </button>
            </div>
          ) : (
            !activeId && (
              <div className="text-[12px]" style={{ color: C.muted }}>קבלת הצעות חדשות מושבתת כרגע.</div>
            )
          )}
        </Modal>
      )}
    </>
  );
}
